export class CallAudio {
  constructor(onFailure, onSuspended) {
    this.onFailure = onFailure;
    this.onSuspended = onSuspended;
    this.closed = false;
    const Context = window.AudioContext || window.webkitAudioContext;
    if (!Context || !window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      throw new Error("Atendimento requer HTTPS, microfone e navegador com Web Audio.");
    }
    try { this.context = new Context({ sampleRate: 16000 }); }
    catch (_) { this.context = new Context(); }
    this.context.onstatechange = () => onSuspended(this.context.state !== "running");
    // Start within the click gesture, before any network request.
    this.context.resume().catch(() => onSuspended(true));
  }
  async prepare() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: {
        channelCount: 1, echoCancellation: true, noiseSuppression: true
      }, video: false });
      if (this.closed) { stream.getTracks().forEach(t => t.stop()); throw new Error("Atendimento cancelado"); }
      this.stream = stream;
      await this.context.audioWorklet.addModule(`${process.env.PUBLIC_URL || ""}/native-call-worklet.js`);
      if (this.closed) throw new Error("Atendimento cancelado");
      this.node = new AudioWorkletNode(this.context, "native-call-pcm", { numberOfInputs: 1,
        numberOfOutputs: 1, outputChannelCount: [1], channelCount: 1 });
      this.source = this.context.createMediaStreamSource(stream);
      this.source.connect(this.node);
      this.node.connect(this.context.destination);
      stream.getTracks().forEach(track => { track.onended = () => this.fail("microphone_ended"); });
    } catch (error) { this.close(); throw error; }
  }
  connect(url) {
    if (this.closed) throw new Error("Áudio encerrado");
    this.socket = new WebSocket(url);
    this.socket.binaryType = "arraybuffer";
    this.timeout = setTimeout(() => this.fail("socket_open_timeout"), 8000);
    const start = () => {
      clearTimeout(this.timeout);
      if (this.started) return;
      this.started = true;
      this.node.port.postMessage({ type: "start" });
    };
    this.socket.onopen = start;
    this.node.port.onmessage = ({ data }) => {
      if (this.closed) return;
      if (!(data instanceof ArrayBuffer)) return this.fail(data?.type === "overflow" ? "worklet_overflow" : "invalid_worklet_frame");
      if (this.socket.readyState !== WebSocket.OPEN) return this.fail("browser_socket_not_open");
      if (this.socket.bufferedAmount > 32000) return this.fail("browser_backpressure");
      this.socket.send(data);
      this.node.port.postMessage({ type: "ack" });
    };
    this.socket.onmessage = ({ data }) => {
      if (this.closed) return;
      if (typeof data === "string") {
        try { if (JSON.parse(data).ready) start(); }
        catch (_) { this.fail("invalid_control_message"); }
      } else if (data.byteLength && data.byteLength % 2 === 0 && data.byteLength <= 32000) {
        // A suspended AudioContext cannot drain its port; discard rather than queue indefinitely.
        if (this.context.state !== "running") return;
        this.node.port.postMessage({ type: "pcm", buffer: data }, [data]);
      } else this.fail("invalid_provider_audio");
    };
    this.socket.onerror = () => this.fail("browser_socket_error");
    this.socket.onclose = event => this.fail(`browser_socket_closed_${event.code || 1005}`);
  }
  mute(value) { this.node?.port.postMessage({ type: "mute", value }); }
  async resume() { await this.context.resume(); }
  fail(reason = "audio_failure") { if (!this.closed) { this.close(4000, reason); this.onFailure(reason); } }
  close(code = 1000, reason = "local_close") {
    if (this.closed) return;
    this.closed = true;
    clearTimeout(this.timeout);
    if (this.socket) {
      this.socket.onopen = null; this.socket.onclose = null; this.socket.onerror = null; this.socket.onmessage = null;
      if (this.socket.readyState === WebSocket.OPEN) this.socket.close(code, reason);
      else this.socket.close();
    }
    this.stream?.getTracks().forEach(track => { track.onended = null; track.stop(); });
    this.source?.disconnect();
    if (this.node) { this.node.port.onmessage = null; this.node.port.close(); this.node.disconnect(); }
    this.context.onstatechange = null;
    this.context.close().catch(() => {});
  }
}
